import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Custom hook for fetching and managing CRM lead data
 * Handles data fetching, caching, and rate limit retries
 */
export function useCrmLeadData(leadId) {
  const navigate = useNavigate();
  
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [allLeads, setAllLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Cache refs for static data
  const companiesCache = useRef(null);
  const templatesCache = useRef(null);
  
  // Helper to fetch with retry on 429
  const fetchWithRetry = useCallback(async (url, maxRetries = 3) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await axios.get(url);
      } catch (error) {
        if (error.response?.status === 429 && i < maxRetries - 1) {
          await new Promise(r => setTimeout(r, 1500 * (i + 1)));
          continue;
        }
        throw error;
      }
    }
  }, []);
  
  const fetchAll = useCallback(async (retryCount = 0) => {
    if (!leadId) return;
    setLoading(true);
    
    try {
      // Fetch lead first - critical data
      const leadRes = await fetchWithRetry(`${API}/leads/${leadId}`);
      setLead(leadRes.data);
      setLoading(false);
      
      // Use cached static data if available
      if (companiesCache.current) setCompanies(companiesCache.current);
      if (templatesCache.current) setTemplates(templatesCache.current);
      
      // Fetch lead-specific data sequentially to avoid rate limits
      try {
        const notesRes = await fetchWithRetry(`${API}/lead-notes/${leadId}`);
        setNotes(notesRes.data || []);
      } catch { setNotes([]); }
      
      try {
        const tasksRes = await fetchWithRetry(`${API}/tasks?lead_id=${leadId}`);
        setTasks(tasksRes.data || []);
      } catch { setTasks([]); }
      
      try {
        const commsRes = await fetchWithRetry(`${API}/communications/lead/${leadId}`);
        setCommunications(commsRes.data || []);
      } catch { setCommunications([]); }
      
      try {
        const logsRes = await fetchWithRetry(`${API}/audit-logs/lead/${leadId}`);
        setAuditLogs(logsRes.data || []);
      } catch { setAuditLogs([]); }
      
      // Fetch static data if not cached
      if (!companiesCache.current) {
        try {
          const companiesRes = await fetchWithRetry(`${API}/companies?limit=1000`);
          const companiesData = companiesRes.data?.items || [];
          // Sort companies alphabetically by name
          companiesData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          companiesCache.current = companiesData;
          setCompanies(companiesCache.current);
        } catch { setCompanies([]); }
      }
      
      if (!templatesCache.current) {
        try {
          const templatesRes = await fetchWithRetry(`${API}/templates`);
          templatesCache.current = templatesRes.data || [];
          setTemplates(templatesCache.current);
        } catch { setTemplates([]); }
      }
      
    } catch (error) {
      if (error.response?.status === 429 && retryCount < 3) {
        toast.info('Loading data, please wait...');
        setTimeout(() => fetchAll(retryCount + 1), 3000);
        return;
      }
      if (error.response?.status === 404) {
        toast.error('Lead not found');
        navigate('/admin/leads');
      } else {
        toast.error('Failed to load lead data. Please refresh.');
      }
      setLoading(false);
    }
  }, [leadId, navigate, fetchWithRetry]);
  
  // Fetch all leads for search functionality
  useEffect(() => {
    const fetchAllLeads = async () => {
      try {
        const res = await axios.get(`${API}/leads?limit=1000`);
        setAllLeads(res.data.items || []);
      } catch { /* ignore */ }
    };
    fetchAllLeads();
  }, []);
  
  useEffect(() => {
    if (leadId) fetchAll();
  }, [leadId, fetchAll]);
  
  // Get company for current lead
  const leadCompany = useMemo(() => {
    if (!lead?.company_id || !companies.length) return null;
    return companies.find(c => c.id === lead.company_id);
  }, [lead?.company_id, companies]);
  
  // Get colleagues from same company
  const companyColleagues = useMemo(() => {
    if (!lead?.company_id) return [];
    return allLeads.filter(l => 
      l.company_id === lead.company_id && l.id !== leadId
    ).sort((a, b) => {
      const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
      const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [allLeads, lead?.company_id, leadId]);
  
  // Add a new company to the local list + cache (avoids full refetch)
  const addCompanyLocal = useCallback((newCompany) => {
    const updated = [...(companiesCache.current || companies), newCompany]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    companiesCache.current = updated;
    setCompanies(updated);
  }, [companies]);

  return {
    lead,
    setLead,
    notes,
    tasks,
    communications,
    auditLogs,
    companies,
    templates,
    allLeads,
    loading,
    leadCompany,
    companyColleagues,
    addCompanyLocal,
    refetch: fetchAll,
  };
}

export default useCrmLeadData;
